<template>
  <uiv-tooltip
    ref="uivPopup"
    v-bind="$attrs"
    v-on="$listeners"
    @show="onShow"
    @hide="onHide">
    <slot/>
  </uiv-tooltip>
</template>

<script>
import { Tooltip } from 'uiv'
import { placePopupBesideTrigger } from './a11y_popup_placement'

export default {
  name: 'A11yTooltip',
  components: { 'uiv-tooltip': Tooltip },
  inheritAttrs: false,
  methods: {
    onShow() {
      this.a11yDescribe()
      // WCAG 1.4.13 (Dismissible): content shown on hover/focus must be
      // dismissible with Escape without moving pointer or focus. uiv's Popup
      // has no keyboard handling, so add it here at the shared wrapper.
      document.addEventListener('keydown', this.onDocumentKeydown, true)
      // WCAG 1.3.2 (Meaningful Sequence): relocate the popup beside its
      // trigger instead of leaving it at the end of <body> (uiv's default).
      const uiv = this.$refs.uivPopup
      // uiv can cancel a pending hide and re-emit 'show' (fast hover-away-
      // then-back) without running resetPosition(), leaving our placement's
      // viewport-fixed values in place — only (re)place when nothing is
      // already tracking it, or we'd double-apply the scroll offset.
      if (!this._releasePopupPlacement) {
        this._releasePopupPlacement = placePopupBesideTrigger(uiv && uiv.$refs.popup, uiv && uiv.triggerEl)
      }
    },
    onHide() {
      this.a11yUndescribe()
      document.removeEventListener('keydown', this.onDocumentKeydown, true)
      if (this._releasePopupPlacement) {
        this._releasePopupPlacement()
        this._releasePopupPlacement = null
      }
    },
    onDocumentKeydown(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return
      const uiv = this.$refs.uivPopup
      if (uiv && typeof uiv.isShown === 'function' && uiv.isShown()) {
        // Don't steal focus — hide only. Re-showing requires a fresh
        // hover/focus, which satisfies the "dismiss without moving focus" rule.
        uiv.hide()
      }
    },
    a11yDescribe() {
      const uiv = this.$refs.uivPopup
      const popup = uiv && uiv.$refs.popup
      const trigger = uiv && uiv.triggerEl
      if (!popup || !trigger) return
      if (!popup.id) {
        popup.id = `a11y-popup-${this._uid}`
      }
      if (!popup.getAttribute('role')) {
        popup.setAttribute('role', 'tooltip')
      }
      const ids = (trigger.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(Boolean)
      if (!ids.includes(popup.id)) {
        ids.push(popup.id)
      }
      trigger.setAttribute('aria-describedby', ids.join(' '))
    },
    a11yUndescribe() {
      const uiv = this.$refs.uivPopup
      const popup = uiv && uiv.$refs.popup
      const trigger = uiv && uiv.triggerEl
      if (!popup || !trigger) return
      const ids = (trigger.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter((id) => id !== popup.id)
      if (ids.length) {
        trigger.setAttribute('aria-describedby', ids.join(' '))
      } else {
        trigger.removeAttribute('aria-describedby')
      }
    },
  },
  beforeDestroy() {
    this.a11yUndescribe()
    document.removeEventListener('keydown', this.onDocumentKeydown, true)
    if (this._releasePopupPlacement) {
      this._releasePopupPlacement()
      this._releasePopupPlacement = null
    }
  },
}
</script>
